import * as React from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";

import { IconComponent, ImageComponent } from "@app/core";
import { Link as ILink } from "@app/api/core/link";
import { CHEVRON_RIGHT } from "@app/constants/icons";
import { IImage } from "@app/api/core/image";
import { RelAttributes } from "@app/constants";
import { checkExternalUrl } from "@app/api/wp-page-fetcher/utils/check-same-domain";

import { FeaturedFilterLabel, IFeaturedLabelProps } from "../featured-filter-label/featured-filter-label";

import styles from "./arrow-panel-component.module.scss";

export interface IArrowPanelProps extends React.HTMLAttributes<HTMLAnchorElement> {
  fallbackImgUrl?: string;
  image?: IImage;
  link: ILink;
  isExternalUrl?: boolean;
  rounded?: boolean;
  variant?: "categoryBig" | "categorySmall";
  featuredFilter?: IFeaturedLabelProps;
}

const ArrowPanel = (props: IArrowPanelProps) => {
  const { image, fallbackImgUrl, link, rounded, variant, isExternalUrl, featuredFilter, ...rest } = props;
  const cardClassName = clsx(
    styles.card,
    rounded && styles["card--rounded"],
    variant === "categoryBig" && styles["card--big"],
    variant === "categorySmall" && styles["card--small"],
    variant && styles["card--category"]
  );

  const imageClassName = clsx(image ? styles["image-container__image"] : styles["image-container__icon"]);
  const imageSrc = image?.src || fallbackImgUrl || "";

  const linkBodyProps: LinkBodyProps = {
    imageClassName,
    imageSrc,
    linkTitle: link.title,
    image,
    featuredFilter,
    noChevron: variant !== undefined
  };

  if (link.newTab || checkExternalUrl(link.url) || isExternalUrl) {
    return (
      <a
        {...rest}
        href={link.url}
        className={cardClassName}
        target={link.newTab ? "_blank" : undefined}
        rel={link.newTab ? RelAttributes.SPONSORED : ""}
      >
        <LinkBody {...linkBodyProps} />
      </a>
    );
  }

  return (
    <Link to={link.url} className={cardClassName} {...rest}>
      <LinkBody {...linkBodyProps} />
    </Link>
  );
};

type LinkBodyProps = {
  imageClassName: string;
  imageSrc: string;
  linkTitle: string;
  image?: IImage;
  noChevron?: boolean;
  featuredFilter?: IFeaturedLabelProps;
};

const LinkBody = ({ imageClassName, imageSrc, linkTitle, image, noChevron, featuredFilter }: LinkBodyProps) => {
  return (
    <>
      {featuredFilter && <FeaturedFilterLabel {...featuredFilter} corners="diagonal-left" className={styles.filter} />}
      <div className={styles.content}>
        {imageSrc && (
          <div className={styles["image-container"]}>
            <ImageComponent
              src={imageSrc}
              alt={image?.alt || ""}
              title={image?.title || ""}
              forceImageTag
              className={imageClassName}
              isBlocking
            />
          </div>
        )}
        <div className={styles.title}>{linkTitle}</div>
      </div>
      {!noChevron && (
        <div className={styles.icon}>
          <IconComponent icon={CHEVRON_RIGHT} size="10px" strokeColor="#474747" strokeWidth="3px" />
        </div>
      )}
    </>
  );
};

export { ArrowPanel };
